/**
 * 배역 모션 — Mixamo 클립을 받아 리그에 물린다.
 *
 * ## 왜 클립을 가져오는가
 *
 * 배역 모델 7개는 전부 Mixamo 리그(`mixamorig:*`, 65본)인데 들어 있는 클립은 하나뿐이고,
 * 재 보니 엉덩이가 3cm·다리가 9~14도만 움직이는 **대기(idle)** 다. 걷기가 없다.
 *
 * 처음에는 사인 곡선으로 걷기를 **만들었다.** 그런데 뼈의 로컬 축이 리그마다 달라서 +각도가
 * 앞인지 뒤인지 계산으로 알 수 없고, 결국 눈으로 부호를 맞춰야 했다. 같은 팀의
 * `legend-of-fist` 가 이미 Mixamo 클립 327개를 갖고 이 문제를 지나 있었다 —
 * **클립의 트랙은 `mixamorig:*.quaternion` 이라는 뼈 이름으로 물리므로, 리그가 같으면
 * 어느 모델에든 그대로 붙는다.** 만들 이유가 없다.
 *
 * ## legend-of-fist 에서 가져온 세 가지 교훈
 *
 * 1. **`Hips.position` 트랙을 버린다.** walking.glb 는 Z 로 1.70, running.glb 는 3.51 을
 *    전진하는 루트 모션을 갖고 있다. 이 게임은 서버가 보낸 좌표로 그룹을 옮기므로, 그대로
 *    쓰면 몸이 그룹에서 떨어져 나가 미끄러진다. 손가락 트랙도 버린다 — 뼈 30개가 늘 돌면
 *    비용만 든다.
 * 2. **Tripo 재질은 alphaMode:BLEND 로 들어온다.** 그대로 두면 피부가 반투명하게 비친다.
 * 3. **키는 rest 포즈에서 재면 안 된다.** FBX 변환 모델은 rest 스켈레톤의 크기가 클립을
 *    적용한 포즈와 달라서 키가 어긋난다. 클립 한 프레임을 적용한 뒤에 재야 한다.
 */

import * as THREE from 'three';
import { IS_GPU } from './gpu';

/** 클립 이름 -> 파일. 애니메이션만 든 GLB 라 45~235KB 다 */
const FILES = {
  idle: '/anims/idle.glb',
  walk: '/anims/walk.glb',
  run: '/anims/run.glb',
  crouch: '/anims/crouch.glb',
  /** 뒤로 넘어져 누운 자세 루프 — 시신에 쓴다 */
  dead: '/anims/dead.glb',
} as const;

export type ClipName = keyof typeof FILES;
export type Clips = Partial<Record<ClipName, THREE.AnimationClip>>;

/**
 * 제자리 클립으로 다듬는다.
 *
 * `Hips.position` 은 루트 모션이라 버린다(게임이 좌표를 소유한다). 손가락은 이 게임에서
 * 아무도 못 보는데 뼈가 30개다.
 */
function inPlace(clip: THREE.AnimationClip): THREE.AnimationClip {
  clip.tracks = clip.tracks.filter(
    (t) => !/Hips\.position$/.test(t.name) && !/Hand(Thumb|Index|Middle|Ring|Pinky)/.test(t.name),
  );
  return clip;
}

let cache: Promise<Clips> | null = null;

/**
 * 클립을 한 번만 받아 모두가 공유한다. **클립은 데이터**이고 뼈 이름으로 물리므로
 * 아바타마다 따로 받을 이유가 없다 — AnimationMixer 만 각자 갖는다.
 */
export function loadClips(): Promise<Clips> {
  if (cache) return cache;
  cache = (async () => {
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const loader = new GLTFLoader();
    const out: Clips = {};
    await Promise.all(
      (Object.keys(FILES) as ClipName[]).map(async (name) => {
        try {
          const g = await loader.loadAsync(FILES[name]);
          const c = g.animations[0];
          if (c) {
            c.name = name;
            out[name] = inPlace(c);
          }
        } catch (e) {
          // 클립 하나가 없어도 나머지로 굴러가야 한다 — 대기만 있어도 서 있기는 한다
          console.warn('[모션] 클립 로드 실패:', name, e);
        }
      }),
    );
    return out;
  })();
  return cache;
}

/**
 * 머리 뼈를 찾는다.
 *
 * **축은 여기서 주지 않는다.** 처음에는 rest 자세에서 끄덕임 축을 한 번 계산해 돌려줬는데,
 * 그 축은 월드 기준이라 아바타가 도는 순간 틀린다 — 옆을 본 사람은 고개를 좌우로 까딱였다.
 * 축은 매 프레임 «그 사람의 오른쪽»으로 구해야 한다(avatar.ts 의 update 참고).
 */
export function findHead(root: THREE.Object3D): THREE.Bone | null {
  root.updateMatrixWorld(true);
  // `mixamorigHead` 는 맞고 `mixamorigHeadTop_End` 는 아니다(그쪽은 `_End` 로 끝난다)
  return boneOf(root, 'Head');
}

/**
 * 접미사로 뼈를 찾는다.
 *
 * **콜론으로 자르면 안 된다.** GLTFLoader 는 노드 이름을 정규화해서 `mixamorig:Hips` 를
 * **`mixamorigHips`** 로 바꿔 놓는다(애니메이션 트랙 경로에 쓰려고 `:` 같은 문자를 지운다).
 * 처음에 `split(':').pop()` 으로 찾다가 **모든 뼈를 못 찾았고**, 그래서 모델을 세우지도
 * 시선을 돌리지도 못했다. 그 사실이 드러나기까지 브라우저를 여섯 번 왕복했다 — 결국
 * Node 에서 GLB 를 직접 파싱해 이름을 찍어 보고 알았다.
 *
 * 접미사로 끝나는지만 본다. `LeftArm` 은 `mixamorigLeftArm` 에 걸리고 `mixamorigLeftForeArm`
 * 에는 걸리지 않는다(그쪽은 `ForeArm` 으로 끝난다).
 */
function boneOf(root: THREE.Object3D, suffix: string): THREE.Bone | null {
  let found: THREE.Bone | null = null;
  root.traverse((o) => {
    if (found || !(o as THREE.Bone).isBone) return;
    if (o.name.endsWith(suffix)) found = o as THREE.Bone;
  });
  return found;
}

/**
 * 모델을 **세우고 정면을 -Z 로 돌린다.** 회전은 감싸는 그룹에 건다.
 *
 * 왜 자동인가: 받은 GLB 4개는 glTF 공간에서 **누워 있었다.** Armature 노드에 X축 +90도가
 * 걸려 있는데 스켈레톤은 이미 Y-up 이라, 그 회전이 사람을 뒤로 눕힌다. 눈치채지 못하고
 * 키를 재면 **누운 몸의 두께(21cm)** 를 키로 잡아 8배로 확대해 버린다 — 실제로 두 사람이
 * 화면을 가득 채운 채 누워 있었고, 로그는 「스팬 0.210」이라고 알려 주었다.
 *
 * 파일마다 다른 이 문제를 상수로 박으면 다음 모델에서 또 터진다. 그래서 **뼈에서 읽는다**:
 *   - 골반 -> 머리 방향이 위(+Y)여야 한다
 *   - 왼팔 -> 오른팔 방향이 오른쪽(+X)이어야 한다. 그러면 정면이 -Z 다
 *     (three 규약: -Z 를 보고 서면 +X 가 오른손이다)
 */
export function orient(holder: THREE.Object3D, model: THREE.Object3D) {
  const hips = boneOf(model, 'Hips');
  const head = boneOf(model, 'Head');
  if (!hips || !head) return;
  const wp = (b: THREE.Object3D) => b.getWorldPosition(new THREE.Vector3());

  holder.updateMatrixWorld(true);
  const up = wp(head).sub(wp(hips));
  if (up.lengthSq() > 1e-8) {
    up.normalize();
    const q = new THREE.Quaternion().setFromUnitVectors(up, new THREE.Vector3(0, 1, 0));
    // **premultiply** — 월드 축에서 돌려야 한다. 곱하는 순서를 바꾸면 모델의 로컬 축으로
    // 돌아서 엉뚱한 데로 눕는다
    holder.quaternion.premultiply(q);
    holder.updateMatrixWorld(true);
  }

  const la = boneOf(model, 'LeftArm') ?? boneOf(model, 'LeftShoulder');
  const ra = boneOf(model, 'RightArm') ?? boneOf(model, 'RightShoulder');
  if (la && ra) {
    const right = wp(ra).sub(wp(la));
    right.y = 0;
    if (right.lengthSq() > 1e-8) {
      // XZ 평면에서 +X 까지의 각도만큼 Y 로 돈다
      const q = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 1, 0),
        Math.atan2(right.z, right.x),
      );
      holder.quaternion.premultiply(q);
      holder.updateMatrixWorld(true);
    }
  }
}

/**
 * 키를 미터로 맞추고 발을 y=0 에 놓는다. **포즈를 적용하고 세운 뒤에** 부를 것.
 *
 * ## Box3 를 쓰면 안 된다
 *
 * `Box3.setFromObject` 는 스킨 메시에서 **bind 포즈 기하의 상자**를 준다 — 뼈가 어디 있든
 * 상관없이. glTF 는 스킨 메시 노드를 변환 없이 씬 루트에 두고 cm→m 변환을 Armature 에만
 * 걸어 두는 일이 흔해서, 그 상자는 **센티미터 크기**로 나온다. 그걸 믿고 100분의 1 로
 * 줄이면 상자만 맞고 실제로 그려지는 몸은 거대하게 뭉개진다 — 처음에 그렇게 만들어서
 * 두 사람이 화면을 가득 채우고 누워 있었다. 상자는 1.68m 라고 보고하면서.
 *
 * 그래서 **그려지는 것**을 잰다: 아래는 발 뼈의 월드 Y, 위는 `applyBoneTransform` 으로
 * 스킨을 실제로 변형시킨 정점의 최고점(머리카락·두상 포함). legend-of-fist 의 방식이다.
 */
/** 지금 **그려지는** 몸의 발·정수리·키를 잰다. fitHeight 가 전후로 같은 코드를 쓴다 */
export function measure(model: THREE.Object3D) {
  model.updateMatrixWorld(true);
  const w = new THREE.Vector3();
  let lo = Infinity, hi = -Infinity, footY = Infinity, headY: number | null = null;
  model.traverse((o) => {
    if (!(o as THREE.Bone).isBone) return;
    o.getWorldPosition(w);
    if (/Head$/.test(o.name)) headY = w.y;
    if (/(Foot|ToeBase)$/.test(o.name)) footY = Math.min(footY, w.y);
    lo = Math.min(lo, w.y); hi = Math.max(hi, w.y);
  });
  let skinTop = -Infinity;
  model.traverse((o) => {
    const sm = o as THREE.SkinnedMesh;
    if (!sm.isSkinnedMesh) return;
    const pos = sm.geometry.attributes.position;
    const step = Math.max(1, Math.floor(pos.count / 9000));
    for (let i = 0; i < pos.count; i += step) {
      sm.applyBoneTransform(i, w.fromBufferAttribute(pos, i));
      w.applyMatrix4(sm.matrixWorld);
      if (w.y > skinTop) skinTop = w.y;
    }
  });
  const bodySpan = hi - lo || 1;
  const bot = footY < Infinity && footY - lo < 0.25 * bodySpan ? footY : lo;
  const top = skinTop > -Infinity ? skinTop : headY != null ? headY + 0.12 : hi;
  return { bot, top, span: Math.max(0.05, top - bot || 1.5), lo, hi };
}

/**
 * **눕힌다.** 골반->머리 방향을 주어진 수평 방향으로 맞춘다.
 *
 * 왜 필요한가: `orient()` 가 모델을 **세우는 회전**을 holder 에 걸어 둔다(받은 GLB 는 제
 * 공간에서 누워 있어서 그래야 선다). 그 상태에서 누운 클립을 적용하면 두 회전이 상쇄되어
 * **시신이 벌떡 선다** — 실제로 그렇게 나왔다(포즈 최저 0.30 최고 2.04 = 서 있는 키).
 * 그래서 포즈를 적용한 뒤 지금 골격이 향한 방향을 다시 재서 수평으로 눕힌다.
 *
 * @param dir 머리가 향할 **월드** 방향. 수평이어야 한다
 */
export function layFlat(holder: THREE.Object3D, model: THREE.Object3D, dir: THREE.Vector3) {
  const hips = boneOf(model, 'Hips');
  const head = boneOf(model, 'Head');
  if (!hips || !head || !hips.parent) return;
  holder.updateMatrixWorld(true);

  const v = head.getWorldPosition(new THREE.Vector3()).sub(hips.getWorldPosition(new THREE.Vector3()));
  if (v.lengthSq() < 1e-8) return;
  // 월드에서 필요한 회전 R: 지금 골격이 향한 방향 -> 원하는 방향
  const R = new THREE.Quaternion().setFromUnitVectors(v.normalize(), dir.clone().normalize());

  // **골반 뼈를 돌린다. holder 를 돌리면 안 된다.**
  // holder(스킨 메시의 조상)를 돌렸더니 몸이 늘어졌다 — 정점 높이 분포가 0.5~1.5m 에
  // 퍼져서(누운 몸이라면 0~0.4m 여야 한다) 팔다리가 뽑힌 모양이 됐다. bindMatrix 는 바인드
  // 시점의 월드로 굳어 있는데 bindMatrixInverse 는 updateMatrixWorld 마다 갱신되므로,
  // 포즈를 적용한 뒤 조상을 돌리면 그 둘이 어긋난다. 뼈를 돌리면 스켈레톤 안에서 끝나므로
  // 스키닝이 정확히 따라온다.
  //
  // 골반의 **부모 프레임**으로 옮겨야 한다: newLocal = P^-1 * R * P * oldLocal
  const toLocal = (world: THREE.Quaternion) => {
    const P = hips.parent!.getWorldQuaternion(new THREE.Quaternion());
    return P.clone().invert().multiply(world).multiply(P);
  };
  hips.quaternion.premultiply(toLocal(R));
  holder.updateMatrixWorld(true);

  // **롤도 잡아야 한다.** 척추 축만 맞추면 축을 중심으로 몸이 얼마나 굴러 있는지는
  // 정해지지 않는다 — 실제로 몸이 옆으로 누워 왼팔이 바닥 34cm 아래를 뚫었다.
  // 어깨선을 수평으로 만들면 등이나 배가 바닥을 향한다(둘 중 가까운 쪽).
  const la = boneOf(model, 'LeftArm') ?? boneOf(model, 'LeftShoulder');
  const ra = boneOf(model, 'RightArm') ?? boneOf(model, 'RightShoulder');
  if (!la || !ra) return;
  const axis = dir.clone().normalize();
  const shoulder = ra.getWorldPosition(new THREE.Vector3()).sub(la.getWorldPosition(new THREE.Vector3()));
  // 척추 축 성분을 뺀다 — 그 축 주위로만 돌릴 것이므로
  shoulder.sub(axis.clone().multiplyScalar(shoulder.dot(axis)));
  if (shoulder.lengthSq() < 1e-8) return;
  // 척추 축과 +Y 에 모두 수직인 방향이 «수평인 어깨선»이다
  const flat = new THREE.Vector3().crossVectors(axis, new THREE.Vector3(0, 1, 0));
  if (flat.lengthSq() < 1e-8) return;
  flat.normalize();
  if (flat.dot(shoulder) < 0) flat.negate(); // 덜 도는 쪽으로
  hips.quaternion.premultiply(toLocal(new THREE.Quaternion().setFromUnitVectors(shoulder.normalize(), flat)));
  holder.updateMatrixWorld(true);
}

/**
 * 지금 그려지는 몸의 **가장 낮은 점**을 월드 Y 로 준다.
 *
 * `measure()` 는 발 뼈를 기준으로 삼으므로 **누운 몸에는 못 쓴다** — 누우면 발이 최저점이
 * 아니고 등·어깨가 바닥에 닿는다. 시신을 바닥에 내려놓을 때는 스킨 전체의 최저점이 필요하다.
 */
export function skinBounds(root: THREE.Object3D): THREE.Box3 {
  root.updateMatrixWorld(true);
  const w = new THREE.Vector3();
  const box = new THREE.Box3();
  root.traverse((o) => {
    const sm = o as THREE.SkinnedMesh;
    if (!sm.isSkinnedMesh) return;
    const pos = sm.geometry.attributes.position;
    const step = Math.max(1, Math.floor(pos.count / 9000));
    for (let i = 0; i < pos.count; i += step) {
      sm.applyBoneTransform(i, w.fromBufferAttribute(pos, i));
      w.applyMatrix4(sm.matrixWorld);
      box.expandByPoint(w);
    }
  });
  return box;
}

/** 골반 뼈. 접지 기준으로 쓴다 */
export function hipsOf(model: THREE.Object3D): THREE.Bone | null {
  return boneOf(model, 'Hips');
}

/** 최저점만 필요할 때 */
export function lowestSkinY(root: THREE.Object3D): number {
  const b = skinBounds(root);
  return b.isEmpty() ? Infinity : b.min.y;
}

export function fitHeight(model: THREE.Object3D, meters: number, holder: THREE.Object3D) {
  model.updateMatrixWorld(true);
  const m0 = measure(model);
  model.scale.multiplyScalar(meters / m0.span);
  const m1 = measure(model);

  // **정렬은 holder 에 건다. model.position 에 넣으면 안 된다.**
  // orient() 가 holder 를 돌려 세웠으므로 model 의 로컬 Y 는 이미 월드 Y 가 아니다 —
  // `model.position.y` 를 내리면 몸이 **옆으로** 간다. GLB 는 보정량이 1cm 안쪽이라
  // 눈에 안 띄었지만, FBX 는 그 오프셋이 커서 몸이 카메라 앞으로 밀려와 화면을 파란 천으로
  // 가득 채웠다. holder 의 부모(group)는 축이 월드와 같으니 여기 걸면 의도대로 움직인다.
  holder.position.y -= m1.bot - 0.01;

  // **수평 중심도 맞춘다.** 엉덩이 뼈가 원점에서 벗어난 모델이 있다 — 그대로 두면 이름표와
  // 몸이 어긋나고, 서버가 보낸 좌표와 보이는 위치가 달라 「저 사람이 왜 저기 있지」가 된다
  const hips = boneOf(model, 'Hips');
  if (hips) {
    model.updateMatrixWorld(true);
    const w = new THREE.Vector3();
    hips.getWorldPosition(w);
    holder.position.x -= w.x;
    holder.position.z -= w.z;
  }
  holder.updateMatrixWorld(true);
}

/**
 * **텍스처만 갈아 끼운다.** 리깅은 그대로 두고 알베도·노멀맵을 더 좋은 것으로 덮는다.
 *
 * 왜 이 길인가: 모델을 다시 뽑으면 리그가 날아가고 Blender 작업을 또 해야 한다. 그런데
 * 품질을 올리는 것은 대개 **텍스처**다 — 받은 모델은 알베도 1024 한 장이 전부이고 노멀맵이
 * 없다(면수 5천은 실루엣에만 영향을 준다). **UV 가 같으면** 텍스처만 바꿔도 그대로 맞는다.
 * 같은지는 눈으로 알 수 없으므로 `tools/glb-tex.py cmp` 가 UV 좌표를 직접 비교해 판정한다.
 *
 * **flipY 를 false 로 둬야 한다.** glTF 는 UV 원점이 좌상단이라 텍스처를 뒤집지 않는데,
 * `TextureLoader` 는 기본이 뒤집기(true)다. 그대로 쓰면 얼굴이 위아래로 뒤집혀 붙는다.
 */
export interface Skin {
  albedo?: string;
  normal?: string;
  /** 노멀맵 세기. 1 이 원본, 낮추면 요철이 약해진다 */
  normalScale?: number;
  /**
   * 이미지를 위아래로 뒤집어 올릴지. **모델의 UV 공간에 맞춰야 한다.**
   *
   * 같은 메시인데도 glTF 와 FBX 는 UV 의 V 축이 서로 뒤집혀 있다(측정: 뒤집으면 6113개가
   * 100% 일치했다). 새로 받은 2K 이미지는 glTF 쪽 방향이므로(픽셀 비교 RMSE 0.018 vs
   * 뒤집기 0.239) glTF 모델에는 그대로(false), FBX 모델에는 뒤집어(true) 올린다.
   */
  flipY?: boolean;
}

export async function applySkin(root: THREE.Object3D, skin: Skin) {
  const loader = new THREE.TextureLoader();
  const load = (url?: string) => (url ? loader.loadAsync(url) : Promise.resolve(null));
  const [alb, nrm] = await Promise.all([load(skin.albedo), load(skin.normal)]);
  for (const t of [alb, nrm]) {
    if (!t) continue;
    t.flipY = skin.flipY ?? false;
    t.anisotropy = 8;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  }
  if (alb) alb.colorSpace = THREE.SRGBColorSpace;
  // 노멀맵은 색이 아니라 **방향 데이터**다. sRGB 로 읽으면 요철이 엉뚱하게 휜다
  if (nrm) nrm.colorSpace = THREE.NoColorSpace;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      const mat = m as THREE.MeshStandardMaterial;
      if (!mat) continue;
      if (alb) mat.map = alb;
      if (nrm) {
        mat.normalMap = nrm;
        mat.normalScale = new THREE.Vector2(skin.normalScale ?? 1, skin.normalScale ?? 1);
      }
      mat.needsUpdate = true;
    }
  });
}

/**
 * 아바타 재질에 쓸 환경맵.
 *
 * 받은 모델은 **알베도 1024 한 장이 전부**다 — 노멀맵도 러프니스맵도 없고, glTF 재질에
 * `roughnessFactor` 가 없어서 규격 기본값 **1.0(완전 거침)** 이 적용된다. 환경 반사가 없는
 * 어두운 씬에서 그 조합은 분필처럼 납작하게 보인다. Wayne 이 「내가 뽑을 때는 이 정도로
 * 저퀄이 아니었다」고 한 차이의 상당 부분이 이것이다 — Tripo 뷰어는 스튜디오 HDRI 를
 * 깔고 보여 준다.
 *
 * **씬 전체(`scene.environment`)에 걸지 않는다.** 이 게임은 밤의 어두운 폐교이고, 벽·가구까지
 * 환경광을 받으면 그 어둠이 날아간다. 그래서 아바타 재질에만 `envMap` 을 꽂는다.
 */
let avatarEnv: THREE.Texture | null = null;

/** 한 번만 굽는다. 렌더러가 있는 곳(main.ts)에서 부팅 때 부른다 */
export async function primeAvatarEnv(renderer: THREE.WebGLRenderer) {
  if (avatarEnv) return;
  try {
    const { RoomEnvironment } = await import('three/examples/jsm/environments/RoomEnvironment.js');
    // WebGPU 는 제 PMREMGenerator 가 따로 있다(gpu.ts)
    const pmrem = IS_GPU ? new (await import('three/webgpu')).PMREMGenerator(renderer as never) as unknown as THREE.PMREMGenerator : new THREE.PMREMGenerator(renderer);
    avatarEnv = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
  } catch (e) {
    // 없으면 그냥 납작하게 보일 뿐이다 — 게임은 굴러간다
    console.warn('[아바타] 환경맵 생성 실패', e);
  }
}

/**
 * Tripo 모델 손질. `legend-of-fist` 가 겪은 것 그대로다 — 재질이 alphaMode:BLEND 로
 * 들어와서 손대지 않으면 **피부가 반투명하게 비친다.**
 */
export function fixMaterials(root: THREE.Object3D) {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    // 스킨 메시는 뼈가 움직여도 경계 상자가 갱신되지 않는다. 컬링을 켜 두면 화면 안에
    // 있는데도 사라진다 — 사람이 통째로 안 보이는 버그가 이것이다
    mesh.frustumCulled = false;
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      const mat = m as THREE.MeshStandardMaterial;
      if (!mat) continue;
      mat.transparent = false;
      mat.depthWrite = true;
      mat.alphaTest = 0;
      // **러프니스 1.0 은 분필이다.** glTF 에 `roughnessFactor` 가 없으면 규격 기본값이 1.0
      // 인데, 이 모델들은 그 값을 안 넣었다. 옷·피부는 0.6~0.7 쯤이 사람처럼 보인다.
      // 모델이 값을 명시했으면(1.0 이 아니면) 만들지 않는다 — 만든 사람 의도를 존중한다
      if (mat.roughness === 1) mat.roughness = 0.68;
      if (avatarEnv && 'envMap' in mat) {
        mat.envMap = avatarEnv;
        // 어두운 씬이라 세게 넣으면 인물만 붕 뜬다. 형태가 읽힐 만큼만
        mat.envMapIntensity = 0.35;
      }
      if (mat.map) {
        mat.map.anisotropy = 8;
        mat.map.colorSpace = THREE.SRGBColorSpace;
      }
      mat.needsUpdate = true;
    }
  });
}


/**
 * **웅크린 시신.** 누운 클립(dead.glb)은 팔을 벌리고 반듯이 누운 자세라 붙박이장 안에서는
 * T 포즈로 읽혔다(Chris 2026-09-07). 클립을 새로 받는 대신 뼈를 직접 접는다 — 축과 부호는
 * 페이지 안에서 손·무릎 위치를 재서 정했다(당시 탐색 스크립트, 지금은 없음):
 *   팔 X+ → 손이 발 쪽으로(옆구리에 붙음) · 아래팔 Z− → 손이 가슴 위로
 *   허벅지 X− → 무릎이 위(가슴 쪽)로 · 정강이 X+ → 발이 엉덩이 쪽으로 · 척추 X− → 머리를 든다
 * 누운 클립을 적용한 **뒤**, layFlat 전에 부른다.
 */
export function curl(model: THREE.Object3D, k = 1) {
  const rot = (name: string, ax: 'X' | 'Y' | 'Z', ang: number) => {
    const b = boneOf(model, name);
    if (!b) return;
    const axis = ax === 'X' ? new THREE.Vector3(1, 0, 0) : ax === 'Y' ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
    b.quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(axis, ang * k));
  };
  rot('LeftArm', 'X', 1.35); rot('RightArm', 'X', 1.35);
  rot('LeftForeArm', 'Z', -1.4); rot('RightForeArm', 'Z', 1.4);
  rot('LeftUpLeg', 'X', -1.45); rot('RightUpLeg', 'X', -1.45);
  rot('LeftLeg', 'X', 1.6); rot('RightLeg', 'X', 1.6);
  rot('Spine', 'X', -0.5); rot('Spine1', 'X', -0.3);
  model.updateMatrixWorld(true);
}

/** 눕힌 몸을 머리 방향 축으로 굴린다(모로 눕히기). layFlat 뒤에 부른다 */
export function roll(model: THREE.Object3D, headDir: THREE.Vector3, ang: number) {
  const hips = boneOf(model, 'Hips');
  if (!hips || !hips.parent) return;
  const R = new THREE.Quaternion().setFromAxisAngle(headDir.clone().normalize(), ang);
  const P = hips.parent.getWorldQuaternion(new THREE.Quaternion());
  hips.quaternion.premultiply(P.clone().invert().multiply(R).multiply(P));
  model.updateMatrixWorld(true);
}


/* ════════════════════════════════════════════════════════════════════
 * 관절 포저 — 뼈에 해부학적 한계를 넣고, 관절 각도 JSON 으로 자세를 잡는다.
 *
 * Chris 2026-09-08: 「레그돌 자체에 관절을 넣어서 조정을 못하냐」. 클립 한 프레임을
 * 얼려 눕히는 방식은 클립에 있는 자세밖에 못 만들고, 뼈를 그냥 돌리면 무릎이 옆으로
 * 꺾인다(09-07 curl 사고). 그래서 **관절마다 어떤 축이 굽힘·벌림·비틀림이고 범위가
 * 얼마인지**를 표로 두고, 그 범위 안에서만 돌린다. 축과 부호는 페이지 안에서 손·무릎
 * 위치를 재서 정했다(당시 탐색 스크립트, 2026-09-07 — 지금은 없음):
 *   팔 X+ → 손이 발 쪽(팔을 내림) · 팔 Z− → 손이 앞(+Y) · 아래팔 Z− → 굽힘
 *   허벅지 X− → 무릎이 앞(+Y) · 정강이 X+ → 발이 뒤 · 척추 X− → 앞으로 굽힘
 * 오른쪽은 좌우 대칭이라 벌림(Z)·비틀림(Y) 의 부호만 뒤집는다.
 * 각도는 **바인드 자세(T 포즈) 기준**이다. 클립 위에 얹지 않는다.
 * ════════════════════════════════════════════════════════════════════ */

export type JointParam = { axis: 'X' | 'Y' | 'Z'; sign: 1 | -1; min: number; max: number };
export type JointSpec = Record<string, JointParam>;
/** 관절 이름(mixamorig 접미사) → 파라미터. 왼쪽 기준. */
const JOINT_L: Record<string, JointSpec> = {
  Arm:      { down: { axis: 'X', sign: 1, min: 0, max: 1.55 }, fwd: { axis: 'Z', sign: -1, min: -0.6, max: 1.7 }, twist: { axis: 'Y', sign: 1, min: -0.9, max: 0.9 } },
  ForeArm:  { flex: { axis: 'Z', sign: -1, min: 0, max: 2.5 } },
  Hand:     { flex: { axis: 'Z', sign: -1, min: -0.6, max: 0.9 } },
  UpLeg:    { flex: { axis: 'X', sign: -1, min: -0.3, max: 2.1 }, abd: { axis: 'Z', sign: 1, min: -0.35, max: 0.8 }, twist: { axis: 'Y', sign: 1, min: -0.6, max: 0.6 } },
  Leg:      { flex: { axis: 'X', sign: 1, min: 0, max: 2.45 } },
  // 발목: 굽힘 + 좌우 젖힘(안쪽·바깥쪽) + 돌림. 굽힘만 있어서 발이 옆으로 안 갔다(Chris 09-08)
  Foot:     { flex: { axis: 'X', sign: 1, min: -0.5, max: 0.7 }, side: { axis: 'Z', sign: 1, min: -0.6, max: 0.6 }, twist: { axis: 'Y', sign: 1, min: -0.7, max: 0.7 } },
  ToeBase:  { flex: { axis: 'X', sign: 1, min: -0.4, max: 0.9 } },
};
const JOINT_C: Record<string, JointSpec> = {
  Spine:  { flex: { axis: 'X', sign: -1, min: -0.35, max: 0.6 }, side: { axis: 'Z', sign: 1, min: -0.4, max: 0.4 }, twist: { axis: 'Y', sign: 1, min: -0.5, max: 0.5 } },
  Spine1: { flex: { axis: 'X', sign: -1, min: -0.3, max: 0.5 }, side: { axis: 'Z', sign: 1, min: -0.35, max: 0.35 }, twist: { axis: 'Y', sign: 1, min: -0.5, max: 0.5 } },
  Spine2: { flex: { axis: 'X', sign: -1, min: -0.3, max: 0.5 }, side: { axis: 'Z', sign: 1, min: -0.35, max: 0.35 }, twist: { axis: 'Y', sign: 1, min: -0.5, max: 0.5 } },
  Neck:   { flex: { axis: 'X', sign: -1, min: -0.6, max: 0.7 }, side: { axis: 'Z', sign: 1, min: -0.5, max: 0.5 }, twist: { axis: 'Y', sign: 1, min: -1.0, max: 1.0 } },
  Head:   { flex: { axis: 'X', sign: -1, min: -0.5, max: 0.6 }, side: { axis: 'Z', sign: 1, min: -0.4, max: 0.4 }, twist: { axis: 'Y', sign: 1, min: -0.8, max: 0.8 } },
};
/**
 * 손가락 (Chris 09-08 「손가락 관절도 구부릴 수 있어야」). 축은 han.glb 에서 쟀다:
 * 마디 X+ → 손끝이 손바닥 쪽으로 접힘, Z → 옆으로 벌어짐. 엄지도 같다. 마디 이름은 `HandIndex1..3`.
 */
const FINGERS = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'] as const;
const fingerJoints = (): Record<string, JointSpec> => {
  const out: Record<string, JointSpec> = {};
  for (const f of FINGERS) for (const seg of [1, 2, 3]) {
    const spec: JointSpec = { flex: { axis: 'X', sign: 1, min: -0.2, max: f === 'Thumb' ? 1.3 : 1.7 } };
    if (seg === 1) spec.abd = { axis: 'Z', sign: 1, min: f === 'Thumb' ? -0.7 : -0.35, max: f === 'Thumb' ? 0.7 : 0.35 };
    out[`Hand${f}${seg}`] = spec;
  }
  return out;
};

/** 전체 관절 표. 키는 `Left*`·`Right*`·중심 이름 */
export const JOINTS: Record<string, JointSpec> = (() => {
  const out: Record<string, JointSpec> = { ...JOINT_C };
  for (const [k, spec] of Object.entries({ ...JOINT_L, ...fingerJoints() })) {
    out['Left' + k] = spec;
    const r: JointSpec = {};
    for (const [p, q] of Object.entries(spec)) r[p] = q.axis === 'X' ? q : { ...q, sign: (q.sign * -1) as 1 | -1 };
    out['Right' + k] = r;
  }
  return out;
})();
/** 손가락 관절 이름인가 (슬라이더 묶음·쥐기 매크로용) */
export const isFingerJoint = (j: string) => /Hand(Thumb|Index|Middle|Ring|Pinky)\d$/.test(j);

/**
 * 관절 → 파라미터 각도. 특수 키:
 *  `__root` { x, y, z, yaw, roll } 몸 전체 이동(m, 그룹 프레임)·머리 방향 회전·굴림
 *  `__offset` { 관절: [x,y,z] } 뼈 위치 오프셋(로컬)   `__scale` { 관절: [sx,sy,sz] } 뼈 길이·굵기
 */
export type Pose = Record<string, Record<string, number>> & { __root?: { x?: number; y?: number; z?: number; yaw?: number; roll?: number }; __offset?: Record<string, number[]>; __scale?: Record<string, number[]>;
  /** 한계를 끄고 기즈모로 돌린 관절의 **원 회전**(바인드 기준 사원수). 오일러로 풀면 90도 근처에서 갑자기 뒤집혀서(짐벌) 몸이 꼬였다 — 그대로 저장한다 */
  __quat?: Record<string, number[]> };

export type RestBone = { q: THREE.Quaternion; p: THREE.Vector3; s: THREE.Vector3 };
export type RestPose = Map<string, RestBone>;
/** 바인드 자세의 뼈 회전·위치·스케일을 기억한다 — 포즈는 여기서 시작한다 */
export function captureRest(model: THREE.Object3D): RestPose {
  const m: RestPose = new Map();
  model.traverse((o) => { if ((o as THREE.Bone).isBone) m.set(o.name, { q: o.quaternion.clone(), p: o.position.clone(), s: o.scale.clone() }); });
  return m;
}

const AXIS = { X: new THREE.Vector3(1, 0, 0), Y: new THREE.Vector3(0, 1, 0), Z: new THREE.Vector3(0, 0, 1) };
/**
 * **관절 한계 켬/끔.** 켜면 해부학적 범위로 자른다(기본). 포저에서 자유롭게 돌리고 싶을 때 끈다 —
 * Chris 09-08 「왜 360도까지 안 돌아가냐」. 끄면 어떤 각도든 그대로 들어간다.
 */
export let jointLimits = true;
export function setJointLimits(on: boolean) { jointLimits = on; }
const clamp = (v: number, a: number, b: number) => (jointLimits ? Math.min(b, Math.max(a, v)) : v);

/**
 * 관절 각도 JSON 을 한계로 잘라 뼈에 넣는다. 적지 않은 관절은 바인드 자세로 돌아간다.
 * 같은 관절의 파라미터는 굽힘 → 벌림 → 비틀림 순으로 곱한다.
 */
export function applyPose(model: THREE.Object3D, rest: RestPose, pose: Pose) {
  for (const [name, r] of rest) {
    const b = boneOf(model, name);
    if (b) { b.quaternion.copy(r.q); b.position.copy(r.p); b.scale.copy(r.s); }
  }
  // 기즈모가 만든 위치 오프셋·길이(스케일). 키는 `__offset`·`__scale`, 값은 관절 → [x,y,z]
  const off = (pose as unknown as { __offset?: Record<string, number[]> }).__offset ?? {};
  const scl = (pose as unknown as { __scale?: Record<string, number[]> }).__scale ?? {};
  for (const [joint, v] of Object.entries(off)) { const b = boneOf(model, joint); if (b) b.position.add(new THREE.Vector3(v[0], v[1], v[2])); }
  for (const [joint, v] of Object.entries(scl)) { const b = boneOf(model, joint); if (b) b.scale.set(v[0], v[1], v[2]); }
  const rawQ = (pose as { __quat?: Record<string, number[]> }).__quat ?? {};
  for (const [joint, raw] of Object.entries(pose)) {
    if (joint.startsWith('__')) continue;
    if (rawQ[joint]) continue;   // 원 회전이 있으면 그것이 이긴다 (아래에서 적용)
    const params = raw as Record<string, number>;
    const spec = JOINTS[joint];
    const b = boneOf(model, joint);
    if (!spec || !b) continue;
    for (const p of ['down', 'fwd', 'flex', 'abd', 'side', 'twist']) {
      const v = params[p];
      const s = spec[p];
      if (v === undefined || !s) continue;
      const ang = clamp(v, s.min, s.max) * s.sign;
      b.quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(AXIS[s.axis], ang));
    }
  }
  for (const [joint, q] of Object.entries(rawQ)) {
    const b = boneOf(model, joint); const r = rest.get(b?.name ?? '');
    if (b && r) b.quaternion.copy(r.q).multiply(new THREE.Quaternion(q[0], q[1], q[2], q[3]));
  }
  model.updateMatrixWorld(true);
}


/* ════════════════════════════════════════════════════════════════════
 * 외부 클립 리타겟 — Quaternius Universal Animation Library (CC0, Rigify DEF-* 리그)
 * → 우리 Mixamo 리그. three 의 SkeletonUtils.retargetClip 을 쓴다.
 * 파일: public/anims/ual_standard.glb (46클립, 6.7MB — 시신처럼 필요한 곳에서만 게으르게 받는다)
 * 매핑은 **대상 뼈 → 원본 뼈**. 손가락은 안 옮긴다(이 게임에서 아무도 못 본다).
 * ════════════════════════════════════════════════════════════════════ */
const UAL_URL = '/anims/ual_standard.glb';
const UAL_NAMES: Record<string, string> = {
  mixamorigHips: 'DEF-hips', mixamorigSpine: 'DEF-spine001', mixamorigSpine1: 'DEF-spine002', mixamorigSpine2: 'DEF-spine003',
  mixamorigNeck: 'DEF-neck', mixamorigHead: 'DEF-head',
  mixamorigLeftShoulder: 'DEF-shoulderL', mixamorigLeftArm: 'DEF-upper_armL', mixamorigLeftForeArm: 'DEF-forearmL', mixamorigLeftHand: 'DEF-handL',
  mixamorigRightShoulder: 'DEF-shoulderR', mixamorigRightArm: 'DEF-upper_armR', mixamorigRightForeArm: 'DEF-forearmR', mixamorigRightHand: 'DEF-handR',
  mixamorigLeftUpLeg: 'DEF-thighL', mixamorigLeftLeg: 'DEF-shinL', mixamorigLeftFoot: 'DEF-footL', mixamorigLeftToeBase: 'DEF-toeL',
  mixamorigRightUpLeg: 'DEF-thighR', mixamorigRightLeg: 'DEF-shinR', mixamorigRightFoot: 'DEF-footR', mixamorigRightToeBase: 'DEF-toeR',
};
let ualCache: Promise<{ source: THREE.SkinnedMesh; clips: THREE.AnimationClip[] }> | null = null;
function loadUal() {
  if (ualCache) return ualCache;
  ualCache = (async () => {
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const g = await new GLTFLoader().loadAsync(UAL_URL);
    let source: THREE.SkinnedMesh | null = null;
    g.scene.traverse((o) => { if (!source && (o as THREE.SkinnedMesh).isSkinnedMesh) source = o as THREE.SkinnedMesh; });
    if (!source) throw new Error('UAL: 스킨 메시가 없다');
    g.scene.updateMatrixWorld(true);
    return { source, clips: g.animations };
  })();
  ualCache.catch(() => { ualCache = null; });
  return ualCache;
}
/** UAL 클립 하나를 이 모델의 리그로 옮긴다. 이름은 `Death01`, `Sitting_Idle_Loop` 같은 원본 이름 */
export async function loadUalClip(model: THREE.Object3D, name: string): Promise<THREE.AnimationClip | null> {
  const { source, clips } = await loadUal();
  const clip = clips.find((c) => c.name === name);
  if (!clip) { console.warn('[UAL] 없는 클립', name); return null; }
  const { retargetClip, clone } = await import('three/examples/jsm/utils/SkeletonUtils.js');
  // **복제본에서 리타겟한다.** retargetClip 은 샘플링하면서 대상 뼈의 위치를 원본 단위(m)로
  // 덮어쓰고 되돌리지 않는다 — 우리 모델은 cm 단위 리그라 몸이 30m 밖으로 터졌다(09-08).
  const tmp = clone(model);
  tmp.updateMatrixWorld(true);
  let target: THREE.SkinnedMesh | null = null;
  tmp.traverse((o) => { if (!target && (o as THREE.SkinnedMesh).isSkinnedMesh) target = o as THREE.SkinnedMesh; });
  if (!target) return null;
  const out = retargetClip(target, source, clip, { names: UAL_NAMES, hip: 'mixamorigHips', useFirstFramePosition: true });
  // 트랙 이름이 `.bones[mixamorigHips].quaternion` 로 나온다 — 믹서는 모델 루트에 있으므로
  // 노드 이름 경로(`mixamorigHips.quaternion`)로 바꿔야 붙는다. 위치 트랙은 전부 버린다
  out.tracks = out.tracks
    .filter((t) => !/\.position$/.test(t.name))
    .map((t) => { t.name = t.name.replace(/^\.bones\[([^\]]+)\]/, '$1'); return t; });
  out.name = 'ual:' + name;
  return inPlace(out);
}
export const UAL_CLIPS = ['Death01', 'Sitting_Idle_Loop', 'Sitting_Talking_Loop', 'Fixing_Kneeling', 'Crouch_Idle_Loop', 'Roll'];
